/**
 * Custom wishlist, ported from the FF theme and adapted to Horizon.
 *
 * Product handles are what we store, as FF does: they read well in storage and
 * survive product edits. The wishlist page resolves them to product ids and
 * fetches the matching product cards; see WishlistProducts.
 *
 * The list lives in localStorage, so it works the same for guests and for
 * signed-in shoppers and survives login, logout and navigation. It does not
 * follow a shopper to another device: that needs an app proxy to write the
 * list onto the customer, the way FF does it. See docs/wishlist.md.
 */

const STORAGE_KEY = 'shopify-wishlist';
const CHANGE_EVENT = 'wishlist:change';

/** Shopify rejects very long search queries, so the page loads the list in batches. */
const PRODUCTS_PER_REQUEST = 20;

/** Counts the wishlist page renders the "N products" phrase for; see #updateCount. */
const COUNT_SAMPLES = [1, 2, 5, 20];

/** localStorage throws in Safari private mode; fall back to a list that lives for the page. */
let memoryFallback = null;

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeHandles(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();

  return value
    .map((handle) => String(handle ?? '').trim())
    .filter((handle) => {
      if (!handle || handle === 'null' || handle === 'undefined') return false;
      if (seen.has(handle)) return false;
      seen.add(handle);
      return true;
    });
}

export const WishlistStore = {
  /** @returns {string[]} */
  read() {
    if (memoryFallback) return [...memoryFallback];

    let stored;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      memoryFallback = [];
      return [];
    }

    if (!stored) return [];

    try {
      return normalizeHandles(JSON.parse(stored));
    } catch {
      // Older versions of this list (and the Swym export) stored a plain CSV.
      return normalizeHandles(stored.split(','));
    }
  },

  /** @param {string[]} handles */
  write(handles) {
    const normalized = normalizeHandles(handles);

    try {
      if (normalized.length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      memoryFallback = normalized;
    }

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { handles: normalized } })
    );

    return normalized;
  },

  /** @param {string} handle */
  has(handle) {
    return this.read().includes(handle);
  },

  /**
   * @param {string} handle
   * @returns {boolean} whether the product is in the wishlist afterwards
   */
  toggle(handle) {
    if (!handle) return false;

    const handles = this.read();
    const index = handles.indexOf(handle);

    if (index === -1) {
      handles.push(handle);
      this.write(handles);
      return true;
    }

    handles.splice(index, 1);
    this.write(handles);
    return false;
  },

  /** @param {string} handle */
  remove(handle) {
    const handles = this.read();
    const index = handles.indexOf(handle);
    if (index === -1) return;

    handles.splice(index, 1);
    this.write(handles);
  },

  count() {
    return this.read().length;
  },
};

// Another tab changed the list.
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY) return;

  memoryFallback = null;
  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, { detail: { handles: WishlistStore.read() } })
  );
});

/*
 * Sections the theme re-renders after load — the header is hydrated, product
 * sections are re-rendered on variant changes — morph their server markup back
 * over the existing elements without reconnecting them. That markup is the
 * "nothing saved" state, so both elements below watch themselves and apply the
 * saved state again when it happens. Each render stops watching while it writes.
 */

/**
 * Toggle rendered on product cards and on the product page.
 * Expects a single <button> child and a data-product-handle.
 */
class WishlistButton extends HTMLElement {
  #onChange = () => this.#render();
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.#observer.disconnect();
  }

  get #handle() {
    return this.dataset.productHandle || '';
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {Element} */ (event.target).closest('button');
    if (!button || !this.contains(button)) return;

    // Product cards put a full-size link over the whole card.
    event.preventDefault();
    event.stopPropagation();

    WishlistStore.toggle(this.#handle);
  };

  #render() {
    this.#observer.disconnect();

    const button = this.querySelector('button');

    if (button) {
      const isSaved = WishlistStore.has(this.#handle);

      this.classList.toggle('wishlist-button--active', isSaved);
      button.setAttribute('aria-pressed', String(isSaved));

      const label = isSaved ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) button.setAttribute('aria-label', label);
    }

    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['class', 'aria-pressed', 'data-product-handle'],
      childList: true,
      subtree: true,
    });
  }
}

/** Header badge. Hidden while the wishlist is empty. */
class WishlistCount extends HTMLElement {
  #onChange = () => this.#render();
  #observer = new MutationObserver(() => this.#render());

  connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.#observer.disconnect();
  }

  #render() {
    this.#observer.disconnect();

    const count = WishlistStore.count();

    this.textContent = count > 0 ? String(count) : '';
    this.hidden = count === 0;

    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['hidden'],
      childList: true,
      characterData: true,
      subtree: true,
    });
  }
}

/**
 * The wishlist page: turns the saved handles back into product cards.
 *
 * The cards come from templates/search.wishlist.json, a search template with no
 * layout whose only section renders the theme's product card for each result.
 * Asking `/search?view=wishlist` for `id:1 OR id:2` returns just those cards —
 * the same ones the collection and search pages show. (The search page's own
 * section cannot be requested from here: a JSON template section's id is tied
 * to its template, so asking for `main` returns a 404.)
 */
class WishlistProducts extends HTMLElement {
  #onChange = () => this.#syncRenderedCards();

  async connectedCallback() {
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    await this.#load();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get #grid() {
    return this.querySelector('[data-wishlist-grid]');
  }

  get #emptyState() {
    return this.querySelector('[data-wishlist-empty]');
  }

  get #errorState() {
    return this.querySelector('[data-wishlist-error]');
  }

  async #load() {
    const grid = this.#grid;
    if (!grid) return;

    const handles = WishlistStore.read();

    if (handles.length === 0) {
      this.#showState('empty');
      return;
    }

    const products = await this.#resolveProducts(handles);

    // Only a product the store confirms is gone (404) leaves the list. A network
    // error or a failed render must never cost the shopper what they saved.
    const gone = products.filter((entry) => entry.status === 'gone').map((entry) => entry.handle);
    if (gone.length > 0) {
      WishlistStore.write(WishlistStore.read().filter((handle) => !gone.includes(handle)));
    }

    const found = products.filter((entry) => entry.status === 'found');
    const cards = found.length > 0 ? await this.#fetchCards(found) : [];

    if (cards.length > 0) {
      grid.replaceChildren(...cards);
      this.#showState('grid');
      this.#syncRenderedCards();
      return;
    }

    // Saved products exist but could not be shown: say so instead of claiming
    // the list is empty.
    const unresolved = products.some((entry) => entry.status !== 'gone');
    this.#showState(unresolved ? 'error' : 'empty');
  }

  /**
   * Looks every saved handle up through the Ajax product endpoint. The search
   * route filters by product id — `handle:` is not a search field — so the id
   * is what the cards are fetched by.
   * @param {string[]} handles
   * @returns {Promise<Array<{handle: string, status: 'found' | 'gone' | 'error', id?: number}>>}
   */
  #resolveProducts(handles) {
    return Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (response.status === 404) return { handle, status: 'gone' };
          if (!response.ok) return { handle, status: 'error' };

          const product = await response.json();
          return { handle, status: 'found', id: product.id };
        } catch {
          return { handle, status: 'error' };
        }
      })
    );
  }

  /**
   * @param {Array<{handle: string, id?: number}>} products
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchCards(products) {
    const batches = [];

    for (let index = 0; index < products.length; index += PRODUCTS_PER_REQUEST) {
      batches.push(products.slice(index, index + PRODUCTS_PER_REQUEST));
    }

    const rendered = await Promise.all(batches.map((batch) => this.#fetchBatch(batch)));
    const cardsByHandle = new Map(rendered.flat());

    // Keep the order the shopper saved them in.
    return products.flatMap((product) => {
      const card = cardsByHandle.get(product.handle);
      return card ? [card] : [];
    });
  }

  /**
   * @param {Array<{handle: string, id?: number}>} batch
   * @returns {Promise<Array<[string, HTMLElement]>>}
   */
  async #fetchBatch(batch) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('view', 'wishlist');
    url.searchParams.set('type', 'product');
    url.searchParams.set('q', batch.map((product) => `id:${product.id}`).join(' OR '));

    try {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return [];

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const items = parsed.querySelectorAll('[data-wishlist-cards] > li[data-product-handle]');

      return Array.from(items, (item) => [
        item.getAttribute('data-product-handle') || '',
        /** @type {HTMLElement} */ (document.importNode(item, true)),
      ]);
    } catch {
      return [];
    }
  }

  /** Drops cards the shopper removed and falls back to the empty state. */
  #syncRenderedCards() {
    const grid = this.#grid;
    if (!grid || grid.hidden || this.hasAttribute('data-loading')) return;

    const handles = WishlistStore.read();

    for (const card of Array.from(grid.querySelectorAll(':scope > [data-product-handle]'))) {
      if (!handles.includes(card.getAttribute('data-product-handle') || '')) card.remove();
    }

    const remaining = grid.querySelectorAll(':scope > [data-product-handle]').length;

    if (remaining === 0) {
      this.#showState('empty');
      return;
    }

    this.#updateCount(remaining);
  }

  /**
   * Writes "4 produse" next to the title. The section renders the translated
   * phrase for a few sample counts; the sample in the same plural category as
   * `count` gets its number swapped, so RO, RU and EN all read naturally.
   * @param {number} count
   */
  #updateCount(count) {
    const element = this.querySelector('[data-wishlist-count]');
    if (!(element instanceof HTMLElement)) return;

    const rules = new Intl.PluralRules(document.documentElement.lang || undefined);
    const category = rules.select(count);
    const sample = COUNT_SAMPLES.find((value) => rules.select(value) === category) ?? COUNT_SAMPLES.at(-1);
    const phrase = element.getAttribute(`data-count-${sample}`) || '';

    element.textContent = phrase.replace(String(sample), String(count));
    element.hidden = count === 0 || phrase === '';
  }

  /** @param {'grid' | 'empty' | 'error'} state */
  #showState(state) {
    const grid = this.#grid;

    if (grid) {
      if (state !== 'grid') grid.replaceChildren();
      grid.hidden = state !== 'grid';
    }

    const empty = this.#emptyState;
    if (empty) empty.hidden = state !== 'empty';

    const error = this.#errorState;
    if (error) error.hidden = state !== 'error';

    if (state !== 'grid') this.#updateCount(0);

    this.removeAttribute('data-loading');
    this.setAttribute('aria-busy', 'false');
  }
}

for (const [name, constructor] of Object.entries({
  'wishlist-button': WishlistButton,
  'wishlist-count': WishlistCount,
  'wishlist-products': WishlistProducts,
})) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
